const LOCALE = 'de-DE';

const DATE_ONLY_REGEX = /^\d{4}-\d{2}-\d{2}$/;

const isValidDate = (date: Date) => !Number.isNaN(date.getTime());

export const parseDate = (value: string): Date => {
  if (!value) {
    return new Date(NaN);
  }

  if (DATE_ONLY_REGEX.test(value)) {
    const [year, month, day] = value.split('-').map(Number);
    return new Date(year, month - 1, day);
  }

  const parsed = new Date(value);
  return isValidDate(parsed) ? parsed : new Date(NaN);
};

const formatWithOptions = (date: Date, options: Intl.DateTimeFormatOptions) =>
  new Intl.DateTimeFormat(LOCALE, options).format(date);

export const formatDateWithWeekday = (value: string): string => {
  const date = parseDate(value);
  if (!isValidDate(date)) {
    return '';
  }

  const weekday = new Intl.DateTimeFormat(LOCALE, { weekday: 'short' }).format(date);
  const dayMonth = formatWithOptions(date, { day: '2-digit', month: '2-digit' });
  return `${weekday}. ${dayMonth}`;
};

export const formatDateDayMonth = (value: string): string => {
  const date = parseDate(value);
  if (!isValidDate(date)) {
    return '';
  }
  return formatWithOptions(date, { day: '2-digit', month: '2-digit' });
};

export const formatDateDayMonthYear = (value: string): string => {
  const date = parseDate(value);
  if (!isValidDate(date)) {
    return '';
  }
  return formatWithOptions(date, { day: '2-digit', month: '2-digit', year: '2-digit' });
};

export const getYear = (value: string): number | null => {
  const date = parseDate(value);
  return isValidDate(date) ? date.getFullYear() : null;
};

export const isDateThisWeek = (value: string): boolean => {
  const date = parseDate(value);
  if (!isValidDate(date)) {
    return false;
  }

  const today = new Date();
  const startOfToday = new Date(today.getFullYear(), today.getMonth(), today.getDate());
  const startOfWeek = new Date(startOfToday);
  startOfWeek.setDate(startOfToday.getDate() - startOfToday.getDay());

  const targetStartOfDay = new Date(date.getFullYear(), date.getMonth(), date.getDate());
  return targetStartOfDay >= startOfWeek;
};

export const isDateInCurrentYear = (value: string): boolean => {
  const year = getYear(value);
  return year !== null && year === new Date().getFullYear();
};
